import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../lib/cn';

// Simplified essay editor (Tiptap integration can be added later)
export default function EssayResponse({
  value,
  onChange,
  placeholder: placeholderProp,
  minWords = 300,
  maxWords = 500,
  disabled = false,
  className,
}) {
  const { t } = useTranslation();
  const placeholder = placeholderProp || t('essayResponse.placeholder');
  const textareaRef = useRef(null);
  const wordCount = value ? value.trim().split(/\s+/).filter(Boolean).length : 0;

  const getWordCountColor = () => {
    if (wordCount < minWords) return 'text-gray-400 dark:text-gray-500';
    if (wordCount > maxWords) return 'text-amber-500';
    return 'text-green-600 dark:text-green-400';
  };

  const handleFormat = (format) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = value.substring(start, end);

    let newText;
    let newCursorPos;

    switch (format) {
      case 'bold':
        newText = value.substring(0, start) + `**${selectedText}**` + value.substring(end);
        newCursorPos = end + 4;
        break;
      case 'italic':
        newText = value.substring(0, start) + `*${selectedText}*` + value.substring(end);
        newCursorPos = end + 2;
        break;
      case 'quote':
        newText = value.substring(0, start) + `> ${selectedText}` + value.substring(end);
        newCursorPos = end + 2;
        break;
      case 'heading':
        newText = value.substring(0, start) + `## ${selectedText}` + value.substring(end);
        newCursorPos = end + 3;
        break;
      default:
        return;
    }

    onChange(newText);
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  return (
    <div className={cn('rounded-lg border border-gray-200 dark:border-gray-600 overflow-hidden', className)}>
      {/* Toolbar */}
      <div className="flex items-center gap-1 px-3 py-2 bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
        <ToolbarButton
          onClick={() => handleFormat('bold')}
          title={t('essayResponse.bold')}
          disabled={disabled}
        >
          <strong>B</strong>
        </ToolbarButton>
        <ToolbarButton
          onClick={() => handleFormat('italic')}
          title={t('essayResponse.italic')}
          disabled={disabled}
        >
          <em>I</em>
        </ToolbarButton>
        <div className="w-px h-5 bg-gray-300 dark:bg-gray-500 mx-1" />
        <ToolbarButton
          onClick={() => handleFormat('heading')}
          title={t('essayResponse.heading')}
          disabled={disabled}
        >
          H2
        </ToolbarButton>
        <ToolbarButton
          onClick={() => handleFormat('quote')}
          title={t('essayResponse.quote')}
          disabled={disabled}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
          </svg>
        </ToolbarButton>
        <div className="flex-1" />
        <span className="text-xs text-gray-400 dark:text-gray-500">
          {t('essayResponse.markdownSupported')}
        </span>
      </div>

      {/* Editor */}
      <textarea
        ref={textareaRef}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        rows={12}
        className={cn(
          'w-full px-4 py-4 resize-none',
          'bg-white dark:bg-gray-800',
          'text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500',
          'focus:outline-none',
          'disabled:bg-gray-50 disabled:text-gray-400 dark:disabled:bg-gray-700 dark:disabled:text-gray-500',
          'leading-relaxed'
        )}
      />

      {/* Footer */}
      <div className="flex items-center justify-between px-4 py-2 bg-gray-50 dark:bg-gray-700 border-t border-gray-200 dark:border-gray-600">
        <span className="text-xs text-gray-500 dark:text-gray-400">
          {t('essayResponse.wordsExpected', { min: minWords, max: maxWords })}
        </span>
        <span className={cn('text-sm font-medium', getWordCountColor())}>
          {t('essayResponse.wordCount', { count: wordCount })}
        </span>
      </div>
    </div>
  );
}

function ToolbarButton({ children, onClick, title, disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      disabled={disabled}
      className={cn(
        'w-8 h-8 rounded flex items-center justify-center',
        'text-gray-600 hover:text-gray-900 hover:bg-gray-200',
        'dark:text-gray-400 dark:hover:text-white dark:hover:bg-gray-600',
        'transition-colors duration-150',
        'disabled:opacity-50 disabled:cursor-not-allowed'
      )}
    >
      {children}
    </button>
  );
}
